"use client";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import Link from "next/link";
import { IconEye, IconHeart, IconUsers, IconStar } from "@/lib/tabler-icons";
import { SectionHeading } from "@/components/ui/editorial";
import { resumenAcciones } from "../../data/resumenAcciones";

interface ActionCardProps {
	title: string;
	description: string;
	icon: React.ReactNode;
	stats?: string;
	testimonial?: string;
	delay?: number;
}

const ActionCard = ({
	title,
	description,
	icon,
	stats,
	testimonial,
	delay = 0,
}: ActionCardProps) => (
	<motion.div
		initial={{ opacity: 0, y: 20 }}
		whileInView={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.8, delay }}
		viewport={{ once: true }}
	>
		<Card className="p-8 border border-border hover:border-terracotta/40 transition-colors duration-300 relative overflow-hidden group">
			<div className="relative z-10">
				<header className="flex items-center mb-6 gap-4">
					<div className="w-10 h-10 border border-border rounded-full flex items-center justify-center text-terracotta shrink-0 group-hover:bg-terracotta group-hover:text-cream transition-colors duration-300">
						{icon}
					</div>
					<h3 className="font-display text-xl font-semibold text-ink leading-tight">
						{title}
					</h3>
				</header>

				<p className="font-body text-ink/80 leading-relaxed mb-4">
					{description}
				</p>

				{stats && (
					<div className="border-l-2 border-terracotta pl-4 my-4">
						<p className="font-body text-sm text-ink/70 leading-relaxed">
							{stats}
						</p>
					</div>
				)}

				{testimonial && (
					<blockquote className="mt-4 border-l-2 border-border pl-4">
						<p className="font-display italic text-terracotta text-sm leading-relaxed">
							&ldquo;{testimonial}&rdquo;
						</p>
					</blockquote>
				)}
			</div>
		</Card>
	</motion.div>
);

interface ActionsSummarySectionProps {
	showCTA?: boolean;
	showProjectLink?: boolean;
}

export default function ActionsSummarySection({
	showCTA = false,
	showProjectLink = false,
}: ActionsSummarySectionProps) {
	return (
		<section className="px-4 xl:px-0 py-16 border-t border-border">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header Section */}
				<motion.header
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className="mb-16"
				>
					<SectionHeading
						index="02"
						title="Todo lo que no ves"
						kicker="Iniciativa Social"
					/>
					<p className="font-body text-lg mt-6 max-w-4xl leading-relaxed text-muted-foreground">
						Las acciones de TODO LO QUE NO VES ofrecen diferentes formatos para
						sensibilizar, prevenir y reparar las heridas invisibles de la
						infancia. Cada propuesta se adapta a distintos públicos — familias,
						jóvenes, mujeres y comunidad— uniendo arte, reflexión y cuidado
						colectivo.
					</p>

					{/* Link "Conocer el proyecto" - Solo se muestra si showProjectLink es true */}
					{showProjectLink && (
						<motion.div
							initial={{ opacity: 0, y: 10 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.4 }}
							viewport={{ once: true }}
							className="mt-8"
						>
							<Link
								href="/todo-lo-que-no-ves"
								className="inline-flex items-center gap-2 font-body text-sm font-medium text-terracotta hover:text-ink border border-terracotta hover:border-ink px-6 py-3 rounded-full transition-colors duration-200"
							>
								<IconEye className="w-4 h-4" />
								Conocer el proyecto
							</Link>
						</motion.div>
					)}
				</motion.header>

				{/* Actions Grid */}
				<div className="grid md:grid-cols-2 gap-8">
					{resumenAcciones.map((accion, index) => (
						<ActionCard
							key={index}
							title={accion.title}
							description={accion.description}
							icon={<accion.Icon className="w-5 h-5" />}
							stats={accion.stats}
							testimonial={accion.testimonial}
							delay={index * 0.1}
						/>
					))}
				</div>

				{/* Call to Action - Solo se muestra si showCTA es true */}
				{showCTA && (
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.6 }}
						viewport={{ once: true }}
						className="mt-16 pt-16 border-t border-border text-center"
					>
						<div className="max-w-3xl mx-auto space-y-6">
							<div className="w-12 h-12 border border-border rounded-full flex items-center justify-center mx-auto text-terracotta">
								<IconHeart className="w-6 h-6" />
							</div>

							<h3 className="font-display text-3xl font-semibold text-ink">
								¿Quieres participar en nuestras acciones?
							</h3>

							<p className="font-body text-lg text-ink/80 leading-relaxed">
								Únete a nosotros para crear espacios de conciencia, autocuidado y
								transformación social. Cada acción está diseñada para generar
								impacto positivo en la comunidad.
							</p>

							<blockquote className="border-l-2 border-terracotta pl-6 text-left my-8">
								<p className="font-display italic text-terracotta text-xl">
									&ldquo;Transformando heridas invisibles en oportunidades de
									crecimiento&rdquo;
								</p>
							</blockquote>

							<div className="flex flex-col sm:flex-row gap-4 justify-center">
								<Link
									href="#contacto"
									className="inline-flex items-center gap-2 font-body text-sm font-medium bg-ink text-cream px-8 py-3 rounded-full hover:bg-ink/90 transition-colors duration-200"
								>
									<IconUsers className="w-4 h-4" />
									Contactar
								</Link>
								<Link
									href="/todo-lo-que-no-ves"
									className="inline-flex items-center gap-2 font-body text-sm font-medium border border-border text-ink px-8 py-3 rounded-full hover:border-terracotta hover:text-terracotta transition-colors duration-200"
								>
									<IconStar className="w-4 h-4" />
									Ver todas las acciones
								</Link>
							</div>
						</div>
					</motion.div>
				)}
			</div>
		</section>
	);
}
